// InstallPrompt — T-091 (ADR-0007 D5 amended). Offer the install once per
// session on the first /app render after sign-in; never force it, never
// hide it. The human's finding 1: "when we sign in and it is not installed
// on the device, offer it."
//
// The previous version required localStorage["pwa.workoutCompleted"], which
// no code ever wrote, so the card had never been shown to anyone; it also
// called four pwa.install.* keys that existed in no locale.
//
// Rules (T-091 Decision 1–5):
// - trigger: signed-in user, on /app/*, not installed, not standalone,
//   `beforeinstallprompt` captured OR iOS Safari; once per session
//   (sessionStorage flag), so a reload does not re-offer
// - defer while a workout is active; show on the next non-workout render
// - cadence: "Not now" → no re-offer for 7 days; 5 lifetime shows; accepted
//   or `appinstalled` → never
// - copy (COPY-T090-T093 §2 + §7): title, body OR bodyIos (never both, W5),
//   iOS Safari shows the two existing steps pwa.iosStep1/2 (B2) instead of
//   an Install button

import { useEffect, useState, type JSX } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../services/auth";
import { isWorkoutActive, onWorkoutActiveChange } from "../../hooks/workoutActive";
import {
  isSafari,
  isStandalone,
  markOfferedThisSession,
  offeredThisSession,
  recordShown,
  shouldShowPrompt,
  useInstallPrompt,
} from "../../hooks/useInstallPrompt";

function isOnDashboard(pathname: string): boolean {
  return pathname === "/app" || pathname.startsWith("/app/");
}

export function InstallPrompt(): JSX.Element | null {
  const { t } = useI18n();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { event, prompt, isIOS, dismiss, installed } = useInstallPrompt();
  const [workoutActive, setWorkoutActive] = useState(isWorkoutActive);
  const [offered, setOffered] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    setWorkoutActive(isWorkoutActive());
    return onWorkoutActiveChange(() => setWorkoutActive(isWorkoutActive()));
  }, []);

  // A level-earned toast and this card share the bottom of the screen
  // (both fixed bottom-4). The level moment wins: when a workout just
  // earned a tier, the card steps aside for the rest of this session
  // (the session flag is already set, so it is not re-offered until the
  // next session; the lifetime count is not touched again).
  useEffect(() => {
    const onLevel = () => setClosed(true);
    window.addEventListener("calicool:level-earned", onLevel);
    return () => window.removeEventListener("calicool:level-earned", onLevel);
  }, []);

  const iosSafari = isIOS && isSafari();
  const eligible =
    !!user &&
    isOnDashboard(pathname) &&
    !installed &&
    !isStandalone() &&
    !workoutActive &&
    (!!event || iosSafari) &&
    shouldShowPrompt() &&
    !offeredThisSession();

  // Decide once: the first render that is eligible claims this session's
  // offer and counts one lifetime show. Later renders keep showing the card
  // until it is closed, except while a workout is on screen.
  useEffect(() => {
    if (offered || !eligible) return;
    setOffered(true);
    markOfferedThisSession();
    recordShown();
  }, [eligible, offered]);

  if (!offered || closed) return null;
  if (installed || workoutActive) return null;

  const handleNotNow = () => {
    dismiss();
    setClosed(true);
  };

  const handleInstall = async () => {
    const outcome = await prompt();
    // accepted → recorded as installed by the hook (never again);
    // dismissed → recorded as a dismissal (7-day pause). Either way the
    // card goes away for this session.
    if (outcome !== null) setClosed(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="install-prompt-title"
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-md border border-[var(--color-line-strong)] bg-[var(--color-slab)] p-4 shadow-lg"
      style={{ borderRadius: "var(--radius-card)" }}
    >
      <h2
        id="install-prompt-title"
        className="font-display text-lg uppercase tracking-[0.02em] text-[var(--color-ink-hi)]"
      >
        {t("pwa.install.title")}
      </h2>
      <p className="mt-1 font-sans text-sm text-[var(--color-ink-hi)]" data-testid="install-prompt-body">
        {iosSafari ? t("pwa.install.bodyIos") : t("pwa.install.body")}
      </p>
      {iosSafari && (
        <ol className="mt-2 flex flex-col gap-1 font-sans text-xs text-[var(--color-ink-mute)]" data-testid="install-prompt-ios-steps">
          <li>{t("pwa.iosStep1")}</li>
          <li>{t("pwa.iosStep2")}</li>
        </ol>
      )}
      <div className="mt-3 flex gap-3">
        <button
          type="button"
          data-testid="install-prompt-dismiss"
          onClick={handleNotNow}
          className="flex-1 border border-[var(--color-line-strong)] px-4 py-2 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-ink)] hover:bg-[var(--color-slab-2)]"
          style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
        >
          {t("pwa.install.notNow")}
        </button>
        {!iosSafari && (
          <button
            type="button"
            data-testid="install-prompt-confirm"
            onClick={() => void handleInstall()}
            disabled={!event}
            className="flex-1 bg-[var(--color-lime)] px-4 py-2 font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-void)] hover:brightness-110 disabled:opacity-50"
            style={{ borderRadius: "var(--radius-sharp)", minHeight: "44px" }}
          >
            {t("pwa.install.install")}
          </button>
        )}
      </div>
    </div>
  );
}
